import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';

import { ChoirPerformance, EmailAddress, ITPerformance, MediaPerformance, SportTeam, TheatrePerformance, User, FestivalUser, ResearchPerformance, VisualArtPerformance } from '../models';
import { Activity, ChoirEmailType, ITStatus, ITEmailType, MediaStatus, Status, TheatreEmailType, TheatreStatus, MediaEmailType, sportEmailType, ResearchStatus, ResearchEmailType, VisualArtEmailType, VisualArtStatus } from '../enums';
import { ChoirPerformanceService } from './choir-performance-service';
import { DocumentService } from './document-service';
import { ITPerformanceService } from './it-performance-service';
import { MediaPerformanceService } from './media-performance-service';
import { TheatrePerformanceService } from './theatre-performance-service';
import { FestivalUserService } from './festival-user-service';
import { ResearchPerformanceService, UserService, VisualArtPerformanceService } from '.';

@Injectable({
  providedIn: 'root'
})

export class EmailService {

     public collection: string = 'emails';
     public emailAddressesCollection: string = 'emailAddresses';

     constructor(
          private angularFirestore: AngularFirestore,
          private choirPerformanceService: ChoirPerformanceService,
          private documentService: DocumentService,
          private itPerformanceService: ITPerformanceService,
          private mediaPerformanceService: MediaPerformanceService,
          private researchPerformanceService: ResearchPerformanceService,
          private theatrePerformanceService: TheatrePerformanceService,
          private festivalUserService: FestivalUserService,
          private userService: UserService,
          private visualArtPerformanceService: VisualArtPerformanceService) {
     }

     public sendChoirPerformanceEmail = async (performance: ChoirPerformance, emailType: ChoirEmailType): Promise<void> => {
          
          var templateName = ChoirEmailType[emailType];
          var templateData: any = performance;

          // hack to set activity
          templateData.activity = Activity.Choir;
          
          var choirCommitteeEmail = await this.getEmailAddress(this.choirPerformanceService.choirCommitteEmail);
          var choirFinanceEmail = await this.getEmailAddress(this.choirPerformanceService.choirFinanceEmail);

          var performanceCreatorEmail = await this.getFestivalUserEmail(performance.createdBy.reference.id);
          var performanceApproverEmail = await this.getFestivalUserEmail(performance.approver.reference.id);

          switch (emailType) {
               // Performance require Approval --> TO: Approver --> BCC: sfmelbchoir@gmail.com
               case ChoirEmailType.performanceAwaitingApproval: 
                    this.sendEmail(templateName, templateData, [performanceApproverEmail], [choirCommitteeEmail]);
                    break;

               // Performance approved --> TO: performance creator --> BCC: sfmelbchoir@gmail.com
               case ChoirEmailType.performanceApproved: 
               case ChoirEmailType.performancePaymentConfirmed: 
                    this.sendEmail(templateName, templateData, [performanceCreatorEmail], [choirCommitteeEmail]);
                    break;

               // Fee Paid --> TO: Robear --> BCC: sfmelbchoir@gmail.com
               case ChoirEmailType.performancePaymentAdded: 
                    this.sendEmail(templateName, templateData, [choirFinanceEmail], [choirCommitteeEmail]);
                    break;

               // Lyrics Upload --> TO: sfmelbchoir@gmail.com
               case ChoirEmailType.performanceFileUploaded:
                    this.sendEmail(templateName, templateData, [choirCommitteeEmail]);
                    break;

               // Performance Accepted --> TO: Performance creator --> BCC: Performance Approver, sfmelbchoir@gmail.com
               case ChoirEmailType.performanceSubmissionCompleted: 
               case ChoirEmailType.performanceReviewSubmitted:
                    this.sendEmail(templateName, templateData, [performanceCreatorEmail], [performanceApproverEmail, choirCommitteeEmail]);
                    break;

               default:
                    break;
          }
     }

     public triggerChoirEmail = async (performance: ChoirPerformance, status: Status, previousStatus?: Status) => {
          var emailType: ChoirEmailType = null;
  
          if (status == Status.PendingCoordinatorApproval) {
            emailType = ChoirEmailType.performanceAwaitingApproval;
          } else if ((!previousStatus || previousStatus == Status.PendingCoordinatorApproval || 
                     (previousStatus == Status.PendingPayment && !performance.receiptNumber)) 
                     && performance.isApprovalConfirmed) {
              emailType = ChoirEmailType.performanceApproved;
          } else if (status == Status.PendingPayment && performance.receiptNumber) {
              emailType = ChoirEmailType.performancePaymentAdded;
          } else if (previousStatus && previousStatus == Status.PendingPayment && performance.isPaymentConfirmed) {
              emailType = ChoirEmailType.performancePaymentConfirmed;
          } else if (previousStatus && previousStatus == Status.PendingLyrics && performance.fileUrl) {
              emailType = ChoirEmailType.performanceFileUploaded;
          } else if (status == Status.SubmissionCompleted) {
              emailType = ChoirEmailType.performanceSubmissionCompleted;
          } else if (status == Status.LyricsReviewRequired) {
              emailType = ChoirEmailType.performanceReviewSubmitted;
          }
  
          if (emailType !== null) 
              await this.sendChoirPerformanceEmail(performance, emailType);
     }

     public sendITPerformanceEmail = async (performance: ITPerformance, emailType: ITEmailType): Promise<void> => {
          
          var templateName = ITEmailType[emailType];
          var templateData: any = performance;

          // hack to set activity
          templateData.activity = Activity.IT;
          
          var itCommitteeEmail = await this.getEmailAddress(this.itPerformanceService.itCommitteeEmail);
          var itFinanceEmail = await this.getEmailAddress(this.itPerformanceService.itFinanceEmail);

          var performanceCreatorEmail = await this.getFestivalUserEmail(performance.createdBy.reference.id);
          var performanceApproverEmail = await this.getFestivalUserEmail(performance.approver.reference.id);

          switch (emailType) {
               // Performance require Approval --> TO: Approver --> BCC: sfmelbchoir@gmail.com
               case ITEmailType.performanceAwaitingApproval: 
                    this.sendEmail(templateName, templateData, [performanceApproverEmail], [itCommitteeEmail]);
                    break;

               // Performance approved --> TO: performance creator --> BCC: sfmelbchoir@gmail.com
               case ITEmailType.performanceApproved:  
                    this.sendEmail(templateName, templateData, [performanceCreatorEmail], [itCommitteeEmail]);
                    break;

               // Fee Paid --> TO: Robear --> BCC: sfmelbchoir@gmail.com
               case ITEmailType.performancePaymentAdded: 
                    this.sendEmail(templateName, templateData, [itFinanceEmail], [itCommitteeEmail]);
                    break;

               // Lyrics Upload --> TO: sfmelbchoir@gmail.com
               case ITEmailType.performanceFileUploaded:
                    this.sendEmail(templateName, templateData, [itCommitteeEmail]);
                    break;

               // Performance Accepted --> TO: Performance creator --> BCC: Performance Approver, sfmelbchoir@gmail.com
               case ITEmailType.performancePaymentConfirmed:
               case ITEmailType.performanceSubmissionCompleted: 
               case ITEmailType.performanceReviewSubmitted:
                    this.sendEmail(templateName, templateData, [performanceCreatorEmail], [performanceApproverEmail, itCommitteeEmail]);
                    break;

               default:
                    break;
          }
     }

     public sendMediaPerformanceEmail = async (performance: MediaPerformance, emailType: MediaEmailType): Promise<void> => {
          
          var templateName = MediaEmailType[emailType];
          var templateData: any = performance;

          // hack to set activity
          templateData.activity = Activity.Media;
          
          var mediaCommitteeEmail = await this.getEmailAddress(this.mediaPerformanceService.mediaCommitteeEmail);
          var mediaFinanceEmail = await this.getEmailAddress(this.mediaPerformanceService.mediaFinanceEmail);

          var performanceCreatorEmail = await this.getFestivalUserEmail(performance.createdBy.reference.id);
          var performanceApproverEmail = await this.getFestivalUserEmail(performance.approver.reference.id);

          switch (emailType) {
               // Performance require Approval --> TO: Approver --> BCC: sfmelbchoir@gmail.com
               case MediaEmailType.performanceAwaitingApproval: 
                    this.sendEmail(templateName, templateData, [performanceApproverEmail], [mediaCommitteeEmail]);
                    break;

               // Performance approved --> TO: performance creator --> BCC: sfmelbchoir@gmail.com
               case MediaEmailType.performanceApproved:  
                    this.sendEmail(templateName, templateData, [performanceCreatorEmail], [mediaCommitteeEmail]);
                    break;

               // Fee Paid --> TO: Robear --> BCC: sfmelbchoir@gmail.com
               case MediaEmailType.performancePaymentAdded: 
                    this.sendEmail(templateName, templateData, [mediaFinanceEmail], [mediaCommitteeEmail]);
                    break;

               // Lyrics Upload --> TO: sfmelbchoir@gmail.com
               case MediaEmailType.performanceFileUploaded:
                    this.sendEmail(templateName, templateData, [mediaCommitteeEmail]);
                    break;

               // Performance Accepted --> TO: Performance creator --> BCC: Performance Approver, sfmelbchoir@gmail.com
               case MediaEmailType.performancePaymentConfirmed:
               case MediaEmailType.performanceSubmissionCompleted: 
               case MediaEmailType.performanceReviewSubmitted:
                    this.sendEmail(templateName, templateData, [performanceCreatorEmail], [performanceApproverEmail, mediaCommitteeEmail]);
                    break;

               default:
                    break;
          }
     }

     public sendResearchPerformanceEmail = async (performance: ResearchPerformance, emailType: ResearchEmailType): Promise<void> => {
          
          var templateName = ResearchEmailType[emailType];
          var templateData: any = performance;

          // hack to set activity
          templateData.activity = Activity.Research;
          
          var researchCommitteeEmail = await this.getEmailAddress(this.researchPerformanceService.researchCommitteEmail);
          var researchFinanceEmail = await this.getEmailAddress(this.researchPerformanceService.researchFinanceEmail);

          var performanceCreatorEmail = await this.getFestivalUserEmail(performance.createdBy.reference.id);
          var performanceApproverEmail = await this.getFestivalUserEmail(performance.approver.reference.id);

          switch (emailType) {
               // Performance require Approval --> TO: Approver --> BCC: sfmelbchoir@gmail.com
               case ResearchEmailType.performanceAwaitingApproval: 
                    this.sendEmail(templateName, templateData, [performanceApproverEmail], [researchCommitteeEmail]);
                    break;

               // Performance approved --> TO: performance creator --> BCC: sfmelbchoir@gmail.com
               case ResearchEmailType.performanceApproved:  
                    this.sendEmail(templateName, templateData, [performanceCreatorEmail], [researchCommitteeEmail]);
                    break;

               // Fee Paid --> TO: Robear --> BCC: sfmelbchoir@gmail.com
               case ResearchEmailType.performancePaymentAdded: 
                    this.sendEmail(templateName, templateData, [researchFinanceEmail], [researchCommitteeEmail]);
                    break;

               // Lyrics Upload --> TO: sfmelbchoir@gmail.com
               case ResearchEmailType.performanceFileUploaded:
                    this.sendEmail(templateName, templateData, [researchCommitteeEmail]);
                    break;

               // Performance Accepted --> TO: Performance creator --> BCC: Performance Approver, sfmelbchoir@gmail.com
               case ResearchEmailType.performancePaymentConfirmed:
               case ResearchEmailType.performanceSubmissionCompleted: 
               case ResearchEmailType.performanceReviewSubmitted:
                    this.sendEmail(templateName, templateData, [performanceCreatorEmail], [performanceApproverEmail, researchCommitteeEmail]);
                    break;

               default:
                    break;
          }
     }

     public sendTheatrePerformanceEmail = async (performance: TheatrePerformance, emailType: TheatreEmailType): Promise<void> => {
          
          var templateName = TheatreEmailType[emailType];
          var templateData: any = performance;

          // hack to set activity
          templateData.activity = Activity.Theatre;
          
          var theatreCommitteeEmail = await this.getEmailAddress(this.theatrePerformanceService.theatreCommitteEmail);
          var theatreFinanceEmail = await this.getEmailAddress(this.theatrePerformanceService.theatreFinanceEmail);

          var performanceCreatorEmail = await this.getFestivalUserEmail(performance.createdBy.reference.id);
          var performanceApproverEmail = await this.getFestivalUserEmail(performance.approver.reference.id);

          switch (emailType) {
               // Performance require Approval --> TO: Approver --> BCC: sfmelbchoir@gmail.com
               case TheatreEmailType.performanceAwaitingApproval: 
                    this.sendEmail(templateName, templateData, [performanceApproverEmail], [theatreCommitteeEmail]);
                    break;

               // Performance approved --> TO: performance creator --> BCC: sfmelbchoir@gmail.com
               case TheatreEmailType.performanceApproved:  
                    this.sendEmail(templateName, templateData, [performanceCreatorEmail], [theatreCommitteeEmail]);
                    break;

               // Fee Paid --> TO: Robear --> BCC: sfmelbchoir@gmail.com
               case TheatreEmailType.performancePaymentAdded: 
                    this.sendEmail(templateName, templateData, [theatreFinanceEmail], [theatreCommitteeEmail]);
                    break;

               // Lyrics Upload --> TO: sfmelbchoir@gmail.com
               case TheatreEmailType.performanceFileUploaded:
                    this.sendEmail(templateName, templateData, [theatreCommitteeEmail]);
                    break;

               // Performance Accepted --> TO: Performance creator --> BCC: Performance Approver, sfmelbchoir@gmail.com
               case TheatreEmailType.performancePaymentConfirmed:
               case TheatreEmailType.performanceSubmissionCompleted: 
               case TheatreEmailType.performanceReviewSubmitted:
                    this.sendEmail(templateName, templateData, [performanceCreatorEmail], [performanceApproverEmail, theatreCommitteeEmail]);
                    break;

               default:
                    break;
          }
     }

     public sendVisualArtPerformanceEmail = async (performance: VisualArtPerformance, emailType: VisualArtEmailType): Promise<void> => {
          
          var templateName = VisualArtEmailType[emailType];
          var templateData: any = performance;

          // hack to set activity
          templateData.activity = Activity.Media;
          
          var committeeEmail = await this.getEmailAddress(this.visualArtPerformanceService.visualArtCommitteeEmail);
          var financeEmail = await this.getEmailAddress(this.visualArtPerformanceService.visualArtFinanceEmail);

          var performanceCreatorEmail = await this.getFestivalUserEmail(performance.createdBy.reference.id);
          var performanceApproverEmail = await this.getFestivalUserEmail(performance.approver.reference.id);

          switch (emailType) {
               // Performance require Approval --> TO: Approver --> BCC: sfmelbchoir@gmail.com
               case VisualArtEmailType.performanceAwaitingApproval: 
                    this.sendEmail(templateName, templateData, [performanceApproverEmail], [committeeEmail]);
                    break;

               // Performance approved --> TO: performance creator --> BCC: sfmelbchoir@gmail.com
               case VisualArtEmailType.performanceApproved:  
                    this.sendEmail(templateName, templateData, [performanceCreatorEmail], [committeeEmail]);
                    break;

               // Fee Paid --> TO: Robear --> BCC: sfmelbchoir@gmail.com
               case VisualArtEmailType.performancePaymentAdded: 
                    this.sendEmail(templateName, templateData, [financeEmail], [committeeEmail]);
                    break;

               // Lyrics Upload --> TO: sfmelbchoir@gmail.com
               case VisualArtEmailType.performanceFileUploaded:
                    this.sendEmail(templateName, templateData, [committeeEmail]);
                    break;

               // Performance Accepted --> TO: Performance creator --> BCC: Performance Approver, sfmelbchoir@gmail.com
               case VisualArtEmailType.performancePaymentConfirmed:
               case VisualArtEmailType.performanceSubmissionCompleted: 
               case VisualArtEmailType.performanceReviewSubmitted:
                    this.sendEmail(templateName, templateData, [performanceCreatorEmail], [performanceApproverEmail, committeeEmail]);
                    break;

               default:
                    break;
          }
     }

     public triggerTheatreEmail = async (performance: TheatrePerformance, status: TheatreStatus, previousStatus?: TheatreStatus) => {
          var emailType: TheatreEmailType = null;
  
          if (status == TheatreStatus.PendingCoordinatorApproval) {
            emailType = TheatreEmailType.performanceAwaitingApproval;
          } else if ((!previousStatus || previousStatus == TheatreStatus.PendingCoordinatorApproval || 
                     (previousStatus == TheatreStatus.PendingPayment && !performance.receiptNumber)) 
                     && performance.isApprovalConfirmed) {
              emailType = TheatreEmailType.performanceApproved;
          } else if (status == TheatreStatus.PendingPayment && performance.receiptNumber) {
              emailType = TheatreEmailType.performancePaymentAdded;
          } else if (previousStatus && previousStatus == TheatreStatus.PendingPayment && performance.isPaymentConfirmed) {
              emailType = TheatreEmailType.performancePaymentConfirmed;
          } else if (previousStatus && previousStatus == TheatreStatus.PendingScript && performance.fileUrl) {
              emailType = TheatreEmailType.performanceFileUploaded;
          } else if (status == TheatreStatus.SubmissionCompleted) {
              emailType = TheatreEmailType.performanceSubmissionCompleted;
          } else if (status == TheatreStatus.ScriptReviewRequired) {
              emailType = TheatreEmailType.performanceReviewSubmitted;
          }
  
          if (emailType !== null) 
              await this.sendTheatrePerformanceEmail(performance, emailType);
     }

     public triggerITEmail = async (performance: ITPerformance, status: ITStatus, previousStatus?: ITStatus) => {
          var emailType: ITEmailType = null;
  
          // if (status == ITStatus.PendingCoordinatorApproval) {
          //   emailType = TheatreEmailType.performanceAwaitingApproval;
          //} else 
          // if ((!previousStatus || previousStatus == ITStatus.PendingCoordinatorApproval || 
          //            (previousStatus == ITStatus.PendingPayment && !performance.receiptNumber)) 
          //            && performance.isApprovalConfirmed) {
          //     emailType = TheatreEmailType.performanceApproved;
          // } else if (status == ITStatus.PendingPayment && performance.receiptNumber) {
          //     emailType = TheatreEmailType.performancePaymentAdded;
          // } else if (previousStatus && previousStatus == ITStatus.PendingPayment && performance.isPaymentConfirmed) {
          //     emailType = TheatreEmailType.performancePaymentConfirmed;
          // } else if (previousStatus && previousStatus == ITStatus.PendingScript && performance.fileUrl) {
          //     emailType = TheatreEmailType.performanceFileUploaded;
          // } else if (status == ITStatus.SubmissionCompleted) {
          //     emailType = TheatreEmailType.performanceSubmissionCompleted;
          // } else if (status == ITStatus.ScriptReviewRequired) {
          //     emailType = TheatreEmailType.performanceReviewSubmitted;
          //}
  
          if (emailType !== null) 
              await this.sendITPerformanceEmail(performance, emailType);
     }

     public triggerMediaEmail = async (performance: MediaPerformance, status: MediaStatus, previousStatus?: MediaStatus) => {
          var emailType: MediaEmailType = null;
  
          if (status == MediaStatus.PendingCoordinatorApproval) {
            emailType = MediaEmailType.performanceAwaitingApproval;
          } else 
          if ((!previousStatus || previousStatus == MediaStatus.PendingCoordinatorApproval || 
                     (previousStatus == MediaStatus.PendingPayment && !performance.receiptNumber)) 
                     && performance.isApprovalConfirmed) {
              emailType = MediaEmailType.performanceApproved;
          } else if (status == MediaStatus.PendingPayment && performance.receiptNumber) {
              emailType = MediaEmailType.performancePaymentAdded;
          } else if (previousStatus && previousStatus == MediaStatus.PendingPayment && performance.isPaymentConfirmed) {
              emailType = MediaEmailType.performancePaymentConfirmed;
          } else if (previousStatus && previousStatus == MediaStatus.PendingProject && performance.fileUrl) {
              emailType = MediaEmailType.performanceFileUploaded;
          } else if (status == MediaStatus.SubmissionCompleted) {
              emailType = MediaEmailType.performanceSubmissionCompleted;
          } else if (status == MediaStatus.ProjectReviewRequired) {
              emailType = MediaEmailType.performanceReviewSubmitted;
          }
  
          if (emailType !== null) 
              await this.sendMediaPerformanceEmail(performance, emailType);
     }

     public triggerResearchEmail = async (performance: ResearchPerformance, status: ResearchStatus, previousStatus?: ResearchStatus) => {
          var emailType: ResearchEmailType = null;
  
          if (status == ResearchStatus.PendingCoordinatorApproval) {
            emailType = ResearchEmailType.performanceAwaitingApproval;
          } else if ((!previousStatus || previousStatus == ResearchStatus.PendingCoordinatorApproval || 
                     (previousStatus == ResearchStatus.PendingPayment && !performance.receiptNumber)) 
                     && performance.isApprovalConfirmed) {
              emailType = ResearchEmailType.performanceApproved;
          } else if (status == ResearchStatus.PendingPayment && performance.receiptNumber) {
              emailType = ResearchEmailType.performancePaymentAdded;
          } else if (previousStatus && previousStatus == ResearchStatus.PendingPayment && performance.isPaymentConfirmed) {
              emailType = ResearchEmailType.performancePaymentConfirmed;
          } else if (previousStatus && previousStatus == ResearchStatus.PendingDocument && performance.fileUrl) {
              emailType = ResearchEmailType.performanceFileUploaded;
          } else if (status == ResearchStatus.SubmissionCompleted) {
              emailType = ResearchEmailType.performanceSubmissionCompleted;
          } else if (status == ResearchStatus.ScriptReviewRequired) {
              emailType = ResearchEmailType.performanceReviewSubmitted;
          }
  
          if (emailType !== null) 
              await this.sendResearchPerformanceEmail(performance, emailType);
     }

     public triggerVisualArtEmail = async (performance: VisualArtPerformance, status: VisualArtStatus, previousStatus?: VisualArtStatus) => {
          var emailType: VisualArtEmailType = null;
  
          if (status == VisualArtStatus.PendingCoordinatorApproval) {
            emailType = VisualArtEmailType.performanceAwaitingApproval;
          } else if ((!previousStatus || previousStatus == VisualArtStatus.PendingCoordinatorApproval || 
                     (previousStatus == VisualArtStatus.PendingPayment && !performance.receiptNumber)) 
                     && performance.isApprovalConfirmed) {
              emailType = VisualArtEmailType.performanceApproved;
          } else if (status == VisualArtStatus.PendingPayment && performance.receiptNumber) {
              emailType = VisualArtEmailType.performancePaymentAdded;
          } else if (previousStatus && previousStatus == VisualArtStatus.PendingPayment && performance.isPaymentConfirmed) {
              emailType = VisualArtEmailType.performancePaymentConfirmed;
          } else if (previousStatus && previousStatus == VisualArtStatus.PendingProject && performance.fileUrl) {
              emailType = VisualArtEmailType.performanceFileUploaded;
          } else if (status == VisualArtStatus.SubmissionCompleted) {
              emailType = VisualArtEmailType.performanceSubmissionCompleted;
          } else if (status == VisualArtStatus.ProjectReviewRequired) {
              emailType = VisualArtEmailType.performanceReviewSubmitted;
          }
  
          if (emailType !== null) 
              await this.sendVisualArtPerformanceEmail(performance, emailType);
     }

     public sendSportTeamEmail = async (team: SportTeam): Promise<void> => {

          var templateData: any = team;

          var teamCreatorEmail = await this.getUserEmail(team.createdBy.reference.id);
          var teamApproverEmail = await this.getFestivalUserEmail(team.approver.reference.id);

          switch (team.status) {
               case 'Submitted': 
                    this.sendEmail(sportEmailType[sportEmailType.teamAwaitingApproval], templateData, [teamApproverEmail]);
                    break;

               case 'Approved': 
                    this.sendEmail(sportEmailType[sportEmailType.teamApproved], templateData, [teamCreatorEmail]);
                    break;

               default:
                    break;
          }
     }

     private getFestivalUserEmail = async (id: string): Promise<string> => {
          return (await this.documentService.getDocumentById(this.festivalUserService.collection, id) as FestivalUser).email;
     }

     private getUserEmail = async (id: string): Promise<string> => {
          return (await this.documentService.getDocumentById(this.userService.collection, id) as User).email;
     }

     private getEmailAddress = async (id: string) => {
          return (await this.documentService.getDocumentById(this.emailAddressesCollection, id) as EmailAddress).email;
     }

     private sendEmail = (templateName: string, templateData: unknown, to: string[], bcc?: string[]) => {
          
          var emailObj: any = { template: { data: templateData, name: templateName }, to: to };

          if (bcc != null)
               emailObj.bcc = bcc;

          this.angularFirestore.collection(this.collection).add(emailObj);
     }
}